/**
 * @fileoverview The frozen single-source-of-truth BRAND_REGISTRY (ESM).
 *
 * Mirrors `../registry.js` (CJS). Hex values locked to
 * `Puffin Logo COLORS v01.pdf`.
 *
 * ⚠️ `taglines` is a per-LANGUAGE map, never a resolved string — see the
 * canonical `taglines` section in `../registry.js` (AAS-I18N-002 Q-F4).
 * The two copies must stay byte-equivalent in VALUE: `tests/esm/
 * native-import.test.mjs` loads both in one process and asserts the
 * tagline maps are deep-equal, because a change to one copy only is a
 * silent divergence (the ESM copy is what every web app renders).
 *
 * @module @puffin-core/brand-assets/registry
 */

// See ../registry.js for the full prose on the Colony asset model.
// Short version: every app's `public/branding/colony/<appKey>.png` slot
// is populated by `scripts/sync-colony.mjs` so the launcher can render
// each sibling's puffin from the CURRENT server (no cross-domain dep).
const CANONICAL_ASSETS = Object.freeze({
  glyph: '/branding/logo-puffin-icon.png',
  glyph512: '/branding/logo-puffin-512.png',
  favicon16: '/branding/favicon-16x16.png',
  favicon32: '/branding/favicon-32x32.png',
  favicon48: '/branding/favicon-48x48.png',
  appleTouch: '/branding/apple-touch-icon.png',
  androidChrome192: '/branding/android-chrome-192x192.png',
  androidChrome512: '/branding/android-chrome-512x512.png',
  ogCard: '/branding/og-card.png',
});

/**
 * Default vendor legal entity. Every brand inherits this unless it
 * supplies its own `legalEntity` to `makeBrand`. See the CJS sibling
 * in `../registry.js` for the canonical comment.
 */
export const DEFAULT_LEGAL_ENTITY = 'D Town Retail, LLC';

function makeBrand({
  appKey,
  appName,
  wordmarkPrefix,
  wordmarkSuffix,
  // Per-language tagline map — `{ en, es? }`. See ../registry.js.
  taglines,
  tenantSubdomain,
  paletteTokenName,
  brand,
  brandTint,
  brandReadable,
  textOnBrand,
  legalEntity = DEFAULT_LEGAL_ENTITY,
  // LIFE-SHORTLISTED-001 Q-LS-32: no `phone` / `whiteLabel` — see ../registry.js (CJS).
  // LINKABLE-ASSETS-001 E3: `reviewTargets`, EMPTY until Q-LA-6 — see ../registry.js (CJS).
  reviewTargets = [],
}) {
  return Object.freeze({
    appKey,
    appName,
    wordmarkPrefix,
    wordmarkSuffix,
    taglines: Object.freeze({ ...taglines }),
    tenantSubdomain,
    paletteTokenName,
    legalEntity,
    // Each target frozen in place (the literals are this module's own), the list frozen too.
    reviewTargets: Object.freeze(reviewTargets.map(Object.freeze)),
    palette: Object.freeze({
      brand,
      brandTint,
      brandReadable,
      textOnBrand,
    }),
    assets: Object.freeze({
      ...CANONICAL_ASSETS,
      colonyGlyph: `/branding/colony/${appKey}.png`,
    }),
  });
}

export const BRAND_REGISTRY = Object.freeze({
  parcel: makeBrand({
    appKey: 'parcel',
    appName: 'ParcelPuffin',
    wordmarkPrefix: 'Parcel',
    wordmarkSuffix: 'Puffin',
    taglines: { en: 'Enterprise Point of Sale', es: 'Punto de venta empresarial' },
    tenantSubdomain: 'parcelpuffin.com',
    paletteTokenName: '--puffin-black',
    brand: '#000000',
    brandTint: '#D1D3D4',
    brandReadable: '#000000',
    textOnBrand: '#FFFFFF',
  }),

  patrol: makeBrand({
    appKey: 'patrol',
    appName: 'PatrolPuffin',
    wordmarkPrefix: 'Patrol',
    wordmarkSuffix: 'Puffin',
    taglines: {
      en: 'Carrier Audit & Reconciliation',
      es: 'Auditoría y conciliación de transportistas',
    },
    tenantSubdomain: 'patrolpuffin.com',
    paletteTokenName: '--puffin-dark-blue',
    brand: '#1C478E',
    brandTint: '#AFC4E8',
    brandReadable: '#1C478E',
    textOnBrand: '#FFFFFF',
  }),

  payday: makeBrand({
    appKey: 'payday',
    appName: 'PayDayPuffin',
    wordmarkPrefix: 'PayDay',
    wordmarkSuffix: 'Puffin',
    taglines: { en: 'Enterprise Payroll', es: 'Nómina empresarial' },
    tenantSubdomain: 'paydaypuffin.com',
    paletteTokenName: '--puffin-green',
    brand: '#3E8340',
    brandTint: '#A9D6A3',
    brandReadable: '#3E8340',
    textOnBrand: '#FFFFFF',
  }),

  planner: makeBrand({
    appKey: 'planner',
    appName: 'PlannerPuffin',
    wordmarkPrefix: 'Planner',
    wordmarkSuffix: 'Puffin',
    taglines: { en: 'Workforce Optimization', es: 'Optimización del personal' },
    tenantSubdomain: 'plannerpuffin.com',
    paletteTokenName: '--puffin-orange',
    brand: '#E65525',
    brandTint: '#FFD1B3',
    // ECO-QUALITY P3 (2026-08-10): was '#AC401C' — the raw
    // `color-mix(in srgb, brand 75%, black)` darkening. The CJS half was retuned to the
    // value that clears WCAG AA 4.5:1 on white and this mirror was never updated. See the
    // block comment on `lib/registry.js`'s planner entry for the derivation.
    brandReadable: '#CD4C21',
    textOnBrand: '#FFFFFF',
  }),

  prep: makeBrand({
    appKey: 'prep',
    appName: 'PrepPuffin',
    wordmarkPrefix: 'Prep',
    wordmarkSuffix: 'Puffin',
    taglines: { en: 'Enterprise Training & LMS', es: 'Capacitación empresarial y LMS' },
    tenantSubdomain: 'preppuffin.com',
    paletteTokenName: '--puffin-yellow',
    brand: '#FBA825',
    brandTint: '#FFF2B3',
    // ECO-QUALITY P3 (2026-08-10): was '#BC7E1C', the raw 75%-black darkening, which is
    // only **3.42:1** on white and therefore FAILS WCAG AA — in the BROWSER half, which is
    // the half users actually see. Corrected to the CJS half's AA-clearing value (4.64:1).
    brandReadable: '#9E6A17',
    textOnBrand: '#FFFFFF',
  }),

  port: makeBrand({
    appKey: 'port',
    appName: 'PortPuffin',
    wordmarkPrefix: 'Port',
    wordmarkSuffix: 'Puffin',
    // `e` (not `y`) before a word starting with the /i/ sound — «voz e IA».
    taglines: { en: 'Business Voice & AI', es: 'Voz e IA para empresas' },
    tenantSubdomain: 'portpuffin.com',
    paletteTokenName: '--puffin-teal',
    brand: '#06B3B1',
    brandTint: '#AEE3E0',
    // ECO-QUALITY P3 (2026-08-10): was '#058685', the raw 75%-black darkening, which is
    // **4.42:1** on white and therefore just FAILS WCAG AA in the browser half. Corrected
    // to the CJS half's AA-clearing value (4.53:1).
    brandReadable: '#048483',
    textOnBrand: '#FFFFFF',
  }),

  palm: makeBrand({
    appKey: 'palm',
    appName: 'PalmPuffin',
    wordmarkPrefix: 'Palm',
    wordmarkSuffix: 'Puffin',
    taglines: { en: 'Workforce Management', es: 'Gestión del personal' },
    tenantSubdomain: 'palmpuffin.com',
    paletteTokenName: '--puffin-light-blue',
    brand: '#4295D1',
    brandTint: '#BFDDF4',
    // ECO-QUALITY P3 (2026-08-10): was '#32709D' — the raw 75%-black darkening. It clears
    // AA (5.33:1), so this one was a colour MISMATCH rather than a failure: the browser
    // rendered a visibly different blue from the server. Converged on the CJS value.
    brandReadable: '#377CAD',
    textOnBrand: '#FFFFFF',
  }),

  purchase: makeBrand({
    appKey: 'purchase',
    appName: 'PurchasePuffin',
    wordmarkPrefix: 'Purchase',
    wordmarkSuffix: 'Puffin',
    // TERM-002 (PLN-LB-UX-005, owner ruling Q-5-10 a, 2026-09-02): "tenant" is the
    // ecosystem's internal word; the storefront IS what a customer buys.
    taglines: { en: 'Multi-Storefront Commerce', es: 'Comercio multitienda' },
    tenantSubdomain: 'purchasepuffin.com',
    paletteTokenName: '--puffin-red',
    brand: '#CB233D',
    brandTint: '#F5B3BD',
    brandReadable: '#CB233D',
    textOnBrand: '#FFFFFF',
  }),


  // PROSPECT-001 (2026-06-13; brand finalized Phase 15, 2026-06-19) —
  // ProspectPuffin business-development app. Final Driftwood #856D55
  // (--puffin-driftwood); ~4.86:1 on white passes WCAG AA so
  // brandReadable === brand. See ../registry.js (CJS) for the canonical
  // comment. Mirrors the CJS sibling.
  prospect: makeBrand({
    appKey: 'prospect',
    appName: 'ProspectPuffin',
    wordmarkPrefix: 'Prospect',
    wordmarkSuffix: 'Puffin',
    taglines: { en: 'Business Development', es: 'Desarrollo de negocio' },
    tenantSubdomain: 'prospectpuffin.com',
    paletteTokenName: '--puffin-driftwood',
    brand: '#856D55',
    brandTint: '#D8C8B8',
    brandReadable: '#856D55',
    textOnBrand: '#FFFFFF',
  }),


  // TRADEMARK-001 (2026-05-23) — wordmarkPrefix changed from the bare brand word to
  // 'ParcelPuffin' so the displayed wordmark is 'ParcelPuffin Console', never the
  // unprefixed word (GUIDES/shared/standards/trademark-bare-brand-word.md).
  console: makeBrand({
    appKey: 'console',
    appName: 'ParcelPuffin Console',
    wordmarkPrefix: 'ParcelPuffin',
    wordmarkSuffix: 'Console',
    taglines: { en: 'Platform Administration', es: 'Administración de la plataforma' },
    tenantSubdomain: null,
    paletteTokenName: '--puffin-black',
    brand: '#000000',
    brandTint: '#D1D3D4',
    brandReadable: '#000000',
    textOnBrand: '#FFFFFF',
  }),
});
